import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { Role, RoleQueryParams, RoleState, CreateRoleData, UpdateRoleData } from '@/types/role';
import { roleService } from '@/services/roleService';

const initialState: RoleState = {
  items: [],
  total: 0,
  page: 1,
  pageSize: 10,
  totalPages: 0,
  loading: false,
  error: null,
};

export const fetchRoles = createAsyncThunk(
  'role/fetchRoles',
  async (params: RoleQueryParams) => {
    const response = await roleService.getRoles(params);
    return response;
  }
);

export const createRole = createAsyncThunk(
  'role/createRole',
  async (data: CreateRoleData) => {
    const response = await roleService.createRole(data);
    return response;
  }
);

export const updateRole = createAsyncThunk(
  'role/updateRole',
  async ({ id, data }: { id: number; data: UpdateRoleData }) => {
    const response = await roleService.updateRole(id, data);
    return response;
  }
);

export const deleteRole = createAsyncThunk(
  'role/deleteRole',
  async (id: number) => {
    await roleService.deleteRole(id);
    return id;
  }
);

const roleSlice = createSlice({
  name: 'role',
  initialState,
  reducers: {
    setPagination: (state, action) => {
      if (state.page !== action.payload.page || state.pageSize !== action.payload.pageSize) {
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // 获取角色列表
      .addCase(fetchRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.pageSize = action.payload.size;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取角色列表失败';
      })
      // 创建角色
      .addCase(createRole.fulfilled, (state, action) => {
        state.items.push(action.payload);
        state.total += 1;
      })
      // 更新角色
      .addCase(updateRole.fulfilled, (state, action) => {
        const index = state.items.findIndex(role => role.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      // 删除角色
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.items = state.items.filter(role => role.id !== action.payload);
        state.total -= 1;
      });
  },
});

export default roleSlice.reducer;
